import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref, watch } from 'vue'

  import TextRenderer from '@/components/editor/TextRenderer.vue'
  import VideoOrImageRenderer from '@/components/course/VideoOrImageRenderer.vue'
  import DurationInput from '@/components/common/DurationInput.vue'
  import EditAuthors from '@/components/editor/EditAuthors.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import Dropdown from '@/components/common/Dropdown.vue'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import ExpandableTextAreaInput from '@/components/common/ExpandableTextAreaInput.vue'

  import { useRoute } from 'vue-router/composables'
  import useIcons from '@/composables/useIcons'
  import useCourse from '@/composables/useCourse'
  import useEditor from '@/composables/useEditor'
  import useI18n from '@/composables/useI18n'

  import { EditorModule } from '@/store/modules/editor'
  import { UserModule } from '@/store/modules/user'
  import { CourseModule } from '@/store/modules/course'

  import CourseApi from '@/services/api/CourseApi'

  import type { Author } from '@/services/interfaces/Common'
  import type { ConfigurableCourseOption, CourseItem, EditorCoursePayload } from '@/services/interfaces/Course'

  import eventBus from '@/main'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'CreateOrUpdateCourse',
  props: {
    editorTitle: {
      type: String,
      required: true,
    },
    createCourse: {
      type: Boolean,
      default: false,
    },
    updateCourse: {
      type: Boolean,
      default: false,
    },
    showUpdateContentCTA: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update-course-section', 'has-course-changes', 'update-training-content'],
  setup(__props, { emit }) {

const props = __props;

  

  

  const route = useRoute()
  const { checkboxSquare, checkboxSquareChecked, cardEmptyImage } = useIcons()
  const { course, currentCourseId } = useCourse()
  const { hasLocalChanges } = useEditor(route)
  const { translateString } = useI18n()

  const editorCourse = reactive({
    title: '',
    shortTitle: '',
    description: '',
    teaserDescription: '',
    teaserImageUrl: '',
    teaserData: null,
    cardTeaserImageUrl: '',
    cardTeaserData: null,
    teaserVideoUrl: '',
    thumbnailTimestamp: 0,
    hostId: '',
    hostDescription: '',
    authors: [],
    allowFeedback: false,
    free: false,
    locked: false,
    allowExpiringCourse: false,
    state: 'in_progress',
    sendFeedbackEmail: 'support@sachkundegurus.de',
    tags: [],
    downloadables: [],
  } as EditorCoursePayload)
  const creatingOrUpdatingCourse = ref(false)
  const courseUserGroupIds = ref([] as string[])
  const newTagTitle = ref('')
  const creatingTag = ref(false)

  const hostAuthor = computed(() => {
    const host = EditorModule.editorAllAuthors?.find((author) => author.id === editorCourse.hostId)
    return host ? [host] : []
  })

  const courseAuthors = computed(() => {
    const authors: Author[] = []
    editorCourse.authors.forEach((a) => {
      const author = EditorModule.editorAllAuthors?.find((author) => author.id === a.authorId)
      if (author) authors.push(author)
    })
    return authors
  })

  const configurableCourseOptions = computed(
    () =>
      [
        {
          title: translateString('editor.createCourse.courseIsFree'),
          action: 'free',
          checked: editorCourse.free,
        },
        {
          title: translateString('editor.createCourse.allowFeedback'),
          action: 'allowFeedback',
          checked: editorCourse.allowFeedback,
        },
        {
          title: translateString('editor.createCourse.isLocked'),
          action: 'locked',
          checked: editorCourse.locked,
        },
        {
          title: translateString('editor.createCourse.allowExpiringCourse'),
          action: 'allowExpiringCourse',
          checked: editorCourse.allowExpiringCourse,
        },
      ] as ConfigurableCourseOption[],
  )

  const courseStates = ['teaser_ready', 'ready', 'in_progress']
  const stateItems = computed(() => {
    return courseStates.map((state) => ({
      type: state,
      title: stateText.value(state),
      selectable: editorCourse.state !== state,
      selectedBefore: editorCourse.state === state,
    }))
  })

  const stateText = computed(() => {
    return (state: string) => {
      switch (state) {
        case 'in_progress':
          return translateString('editor.createCourse.inProgress')
        case 'ready':
          return translateString('editor.createCourse.ready')
        case 'teaser_ready':
          return translateString('editor.createCourse.teaserReady')
        default:
          return translateString('editor.createCourse.inProgress')
      }
    }
  })

  const unfilledRequiredAndHasNoChanges = computed(() => {
    // for updating course if the course has the teaserVideoUrl we exempt checking the teaserData or teaserImageUrl
    return !(
      editorCourse.title &&
      editorCourse.description &&
      editorCourse.teaserDescription &&
      (props.updateCourse
        ? editorCourse.teaserData || editorCourse.teaserImageUrl || editorCourse.teaserVideoUrl
        : editorCourse.teaserData) &&
      (props.updateCourse
        ? editorCourse.cardTeaserData || editorCourse.cardTeaserImageUrl
        : editorCourse.cardTeaserData) &&
      editorCourse.state &&
      (props.updateCourse ? hasCourseChanges.value : true)
    )
  })

  const allMemberCourseGroups = computed(() => UserModule.userGroups || [])

  const isGroupSelected = computed(() => (groupId: string) => courseUserGroupIds.value.includes(groupId))

  const tagItems = computed(() => CourseModule.tags)

  const isTagSelected = computed(() => (tagId: string) => !!editorCourse.tags.find((t) => t.tagId === tagId))

  const createOrUpdateCourseText = computed(() =>
    props.createCourse
      ? translateString('editor.createCourse.createCourseTitle')
      : translateString('editor.createCourse.updateCourse'),
  )

  const teaserImageUrl = computed(() => {
    return props.createCourse ? cardEmptyImage.value : editorCourse.teaserImageUrl
  })

  const cardTeaserImageUrl = computed(() => {
    return props.createCourse ? cardEmptyImage.value : editorCourse.cardTeaserImageUrl
  })

  const hasCourseChanges = computed(() => hasLocalChanges.value(editorCourse, EditorModule.course))

  const onTeaserImageUpload = (imageData: string) => {
    editorCourse.teaserData = imageData
  }

  const onCardTeaserImageUpload = (imageData: string) => {
    editorCourse.cardTeaserData = imageData
  }

  const removeTeaserImageUrl = () => {
    editorCourse.teaserImageUrl = ''
    editorCourse.teaserData = null
  }

  const removeCardTeaserImageUrl = () => {
    editorCourse.cardTeaserImageUrl = ''
    editorCourse.cardTeaserData = null
  }

  const onTeaserVideoUpload = (url: string) => {
    editorCourse.teaserVideoUrl = url
  }

  const addHost = (hostId: string) => {
    editorCourse.hostId = hostId
  }

  const addCourseAuthor = (authorId: string) => {
    editorCourse.authors.push({
      authorId,
    })
  }

  const removeHost = () => {
    editorCourse.hostId = ''
  }

  const removeCourseAuthor = (authorId: string) => {
    const index = editorCourse.authors.findIndex((a) => a.authorId === authorId)
    if (index > -1) {
      editorCourse.authors.splice(index, 1)
    }
  }

  const configureCourseOption = (action: 'free' | 'allowFeedback' | 'locked' | 'allowExpiringCourse') => {
    editorCourse[action] = !editorCourse[action]
  }

  const setCourseState = (state: 'in_progress' | 'ready' | 'teaser_ready') => {
    editorCourse.state = state
  }

  const onTagClick = (tagId: string) => {
    if (isTagSelected.value(tagId)) {
      const index = editorCourse.tags.findIndex((t) => t.tagId === tagId)
      editorCourse.tags.splice(index, 1)
      return
    }
    editorCourse.tags.push({ tagId })
  }

  const onGroupClick = (groupId: string) => {
    if (isGroupSelected.value(groupId)) {
      const index = courseUserGroupIds.value.findIndex((id) => id === groupId)
      courseUserGroupIds.value.splice(index, 1)
      return
    }
    courseUserGroupIds.value.push(groupId)
  }

  const createTag = () => {
    creatingTag.value = true
    CourseApi.createTag(newTagTitle.value)
      .then((resp) => {
        CourseModule.tags.unshift(resp.tag)
      })
      .catch((error) => {
        if (error.response.status === 400) {
          eventBus.$toasted.error(translateString(`${error.response.data}`), { duration: 8000 })
        } else {
          eventBus.$toasted.error(translateString('editor.createCourse.errorInCreatingTag'), {
            duration: 8000,
          })
        }
      })
      .finally(() => (creatingTag.value = false))
  }

  const setDefaultTeaserImageData = () => {
    // set default image data
    editorCourse.teaserData =
      '{"id":"card_empty_image.png","storage":"store","metadata":{"filename":"card_empty_image.png","size":5820,"mime_type":"image/png"}}'
    editorCourse.cardTeaserData =
      '{"id":"card_empty_image.png","storage":"store","metadata":{"filename":"card_empty_image.png","size":5820,"mime_type":"image/png"}}'
  }

  const prefillCourseDetails = (course: CourseItem) => {
    editorCourse.title = course.title
    editorCourse.shortTitle = course.shortTitle
    editorCourse.description = course.description
    editorCourse.teaserDescription = course.teaserDescription || ''
    editorCourse.teaserImageUrl = course.teaserImageUrl
    editorCourse.cardTeaserImageUrl = course.cardTeaserImageUrl
    editorCourse.teaserVideoUrl = course.teaserVideoUrl || ''
    editorCourse.thumbnailTimestamp = course.thumbnailTimestamp || 0
    editorCourse.hostId = course.host?.id || ''
    editorCourse.hostDescription = course.hostDescription
    editorCourse.authors = course.authors.map((a) => ({ authorId: a.id }))
    editorCourse.allowFeedback = course.allowFeedback
    editorCourse.free = course.free || false
    editorCourse.locked = course.locked
    editorCourse.allowExpiringCourse = course.allowExpiringCourse
    editorCourse.state = course.state
    editorCourse.sendFeedbackEmail = course.sendFeedbackEmail
    editorCourse.tags = course.tags.map((t) => ({ tagId: t.id }))
    editorCourse.downloadables =
      course.downloadables?.map((lessonDownloadable) => ({ lessonId: lessonDownloadable.id })) ?? []
  }

  const createNewCourse = () => {
    CourseApi.createCourse({ course: editorCourse })
      .then(async (resp) => {
        emit('update-course-section', {
          courseId: resp.courseId,
          courseGroupIds: courseUserGroupIds.value,
        })
      })
      .catch((error) => {
        if (error.response.status === 400) {
          eventBus.$toasted.error(translateString(`${error.response.data}`), { duration: 8000 })
        } else {
          eventBus.$toasted.error(translateString('editor.createCourse.errorInCreatingCourse'), {
            duration: 8000,
          })
        }
      })
      .finally(() => (creatingOrUpdatingCourse.value = false))
  }

  const updateExistingCourse = () => {
    const properties = [
      'title',
      'shortTitle',
      'description',
      'teaserDescription',
      'teaserImageUrl',
      'teaserData',
      'cardTeaserImageUrl',
      'cardTeaserData',
      'teaserVideoUrl',
      'thumbnailTimestamp',
      'benefits',
      'hostId',
      'hostDescription',
      'authors',
      'allowFeedback',
      'free',
      'locked',
      'allowExpiringCourse',
      'state',
      'sendFeedbackEmail',
      'tags',
      'downloadables',
    ] as (keyof EditorCoursePayload)[]

    const payload: any = {
      course: {},
    }

    const alwaysSubmitProperties = ['authors', 'tags', 'downloadables']

    properties.forEach((property) => {
      if (
        alwaysSubmitProperties.includes(property) ||
        hasLocalChanges.value(EditorModule.course?.[property], editorCourse[property])
      ) {
        payload.course[property] = editorCourse[property]
      }
    })

    CourseApi.updateCourse(currentCourseId.value, payload)
      .then((resp) => {
        if (resp === 200) {
          CourseModule.getCourse({ courseId: currentCourseId.value })
          eventBus.$toasted.success(translateString('editor.createCourse.updatedTrainingSuccessfully'))
        }
      })
      .finally(() => (creatingOrUpdatingCourse.value = false))
  }

  const submitCourseDetails = () => {
    creatingOrUpdatingCourse.value = true
    if (props.createCourse) {
      createNewCourse()
    } else {
      updateExistingCourse()
      EditorModule.setSubmitCourseChanges(false)
    }
  }

  const discardAllCourseChanges = () => {
    if (course.value) {
      prefillCourseDetails(course.value)
      EditorModule.setDiscardCourseChanges(false)
    }
  }

  const discardCourseChanges = computed(() => EditorModule.discardCourseChanges)
  watch(discardCourseChanges, (value) => {
    if (value) {
      discardAllCourseChanges()
    }
  })

  const submitCourseChanges = computed(() => EditorModule.submitCourseChanges)
  watch(submitCourseChanges, (value) => {
    if (value) {
      submitCourseDetails()
    }
  })

  watch(hasCourseChanges, (value) => {
    emit('has-course-changes', value)
  })

  watch(course, (value) => {
    if (value) {
      prefillCourseDetails(course.value!)
      EditorModule.setEditorCourse(editorCourse)
    }
  })

  onMounted(() => {
    if (props.createCourse) {
      setDefaultTeaserImageData()
    }

    if (props.updateCourse && course.value) {
      prefillCourseDetails(course.value)
      EditorModule.setEditorCourse(editorCourse)
    }
  })

return { __sfc: true,props, emit, route, checkboxSquare, checkboxSquareChecked, cardEmptyImage, course, currentCourseId, hasLocalChanges, translateString, editorCourse, creatingOrUpdatingCourse, courseUserGroupIds, newTagTitle, creatingTag, hostAuthor, courseAuthors, configurableCourseOptions, courseStates, stateItems, stateText, unfilledRequiredAndHasNoChanges, allMemberCourseGroups, isGroupSelected, tagItems, isTagSelected, createOrUpdateCourseText, teaserImageUrl, cardTeaserImageUrl, hasCourseChanges, onTeaserImageUpload, onCardTeaserImageUpload, removeTeaserImageUrl, removeCardTeaserImageUrl, onTeaserVideoUpload, addHost, addCourseAuthor, removeHost, removeCourseAuthor, configureCourseOption, setCourseState, onTagClick, onGroupClick, createTag, setDefaultTeaserImageData, prefillCourseDetails, createNewCourse, updateExistingCourse, submitCourseDetails, discardAllCourseChanges, discardCourseChanges, submitCourseChanges, computed, onMounted, reactive, ref, watch, TextRenderer, VideoOrImageRenderer, DurationInput, EditAuthors, SVGRenderer, Dropdown, KetchUpButton, LoadingSpinner, ExpandableTextAreaInput, useRoute, useIcons, useCourse, useEditor, useI18n, EditorModule, UserModule, CourseModule, CourseApi, eventBus }
}

})